import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, DollarSign, Upload, Loader2 } from "lucide-react";
import { fmt } from "@/lib/format";
import { formatApiError } from "@/lib/errors";

export default function AdminProperties() {
  const [list, setList] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payoutFor, setPayoutFor] = useState(null);
  const [form, setForm] = useState(empty());

  function empty() {
    return {
      name: "",
      location: "",
      description: "",
      total_bricks: 100,
      price_per_brick: 100,
      roi_percentage: 7,
      rental_yield: 5,
      image_url: "",
    };
  }

  const load = () => api.get("/properties").then(({ data }) => setList(data));
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    try {
      await api.post("/properties", { ...form, total_bricks: parseInt(form.total_bricks), price_per_brick: parseFloat(form.price_per_brick), roi_percentage: parseFloat(form.roi_percentage), rental_yield: parseFloat(form.rental_yield) });
      toast.success("Property created");
      setCreateOpen(false);
      setForm(empty());
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to create"));
    }
  };

  const update = async () => {
    try {
      await api.patch(`/properties/${editing.id}`, {
        name: form.name,
        location: form.location,
        description: form.description,
        price_per_brick: parseFloat(form.price_per_brick),
        roi_percentage: parseFloat(form.roi_percentage),
        rental_yield: parseFloat(form.rental_yield),
        image_url: form.image_url || undefined,
      });
      if (parseInt(form.total_bricks) !== editing.total_bricks) {
        await api.patch(`/properties/${editing.id}/bricks`, { total_bricks: parseInt(form.total_bricks) });
      }
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to update"));
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this property? Only possible if no bricks are sold.")) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to delete"));
    }
  };

  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      location: p.location,
      description: p.description,
      total_bricks: p.total_bricks,
      price_per_brick: p.price_per_brick,
      roi_percentage: p.roi_percentage,
      rental_yield: p.rental_yield,
      image_url: p.image_url || "",
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline">Catalog</div>
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mt-2">
            Properties
          </h1>
        </div>
        <Button
          className="btn-gold rounded-full"
          data-testid="admin-create-property-btn"
          onClick={() => {
            setForm(empty());
            setCreateOpen(true);
          }}
        >
          <Plus className="w-4 h-4 mr-1" /> New property
        </Button>
      </div>

      <div className="space-y-3">
        {list.map((p) => (
          <div key={p.id} className="bp-surface rounded-xl p-4 flex flex-wrap gap-4 items-center" data-testid={`admin-prop-${p.id}`}>
            <img src={p.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-white font-medium">{p.name}</div>
              <div className="text-xs text-gray-400">{p.location}</div>
            </div>
            <div className="text-xs text-gray-400">
              <div className="font-mono text-white">{p.sold_bricks}/{p.total_bricks}</div>
              <div>sold</div>
            </div>
            <div className="text-xs text-gray-400">
              <div className="font-mono text-[#D4AF37]">{fmt(p.price_per_brick)}</div>
              <div>per brick</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setPayoutFor(p)} data-testid={`payout-${p.id}`}>
                <DollarSign className="w-3 h-3 mr-1" /> Payout
              </Button>
              <Button size="sm" variant="outline" onClick={() => startEdit(p)} data-testid={`edit-${p.id}`}>
                <Pencil className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => del(p.id)} data-testid={`delete-${p.id}`}>
                <Trash2 className="w-3 h-3 text-red-400" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* CREATE / EDIT DIALOG */}
      <Dialog
        open={createOpen || !!editing}
        onOpenChange={(o) => {
          if (!o) {
            setCreateOpen(false);
            setEditing(null);
          }
        }}
      >
        <DialogContent className="bg-[#0B1120] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit property" : "New property"}</DialogTitle>
          </DialogHeader>
          <PropertyForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateOpen(false); setEditing(null); }}>Cancel</Button>
            <Button className="btn-gold rounded-full" onClick={editing ? update : create} data-testid="save-property-btn">
              {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PAYOUT DIALOG */}
      <PayoutDialog prop={payoutFor} onClose={() => setPayoutFor(null)} onDone={load} />
    </div>
  );
}

function PropertyForm({ form, setForm }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  const [uploading, setUploading] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${backend}${data.url}`;
      set("image_url", url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — object storage may be unavailable");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <Label>Name</Label>
        <Input data-testid="form-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
      </div>
      <div className="col-span-2">
        <Label>Location</Label>
        <Input data-testid="form-location" value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1 bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
      </div>
      <div className="col-span-2">
        <Label>Description <span className="text-gray-500 text-xs">(optional)</span></Label>
        <Textarea data-testid="form-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
      </div>
      <div>
        <Label>Total bricks</Label>
        <Input data-testid="form-bricks" type="number" value={form.total_bricks} onChange={(e) => set("total_bricks", e.target.value)} className="mt-1 bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
      </div>
      <div>
        <Label>Price per brick (₦)</Label>
        <Input data-testid="form-price" type="number" value={form.price_per_brick} onChange={(e) => set("price_per_brick", e.target.value)} className="mt-1 bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
      </div>
      <div>
        <Label>Annual ROI %</Label>
        <Input type="number" step="0.1" value={form.roi_percentage} onChange={(e) => set("roi_percentage", e.target.value)} className="mt-1 bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
      </div>
      <div>
        <Label>Rental yield %</Label>
        <Input type="number" step="0.1" value={form.rental_yield} onChange={(e) => set("rental_yield", e.target.value)} className="mt-1 bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
      </div>
      <div className="col-span-2">
        <Label>Property image</Label>
        <div className="mt-1 flex gap-3 items-start">
          <div className="flex-1">
            <Input
              placeholder="Paste image URL or upload file →"
              value={form.image_url}
              onChange={(e) => set("image_url", e.target.value)}
              className="bg-[#050914] border-white/10 focus:border-[#D4AF37]"
            />
            <div className="mt-2">
              <label
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] text-xs cursor-pointer hover:bg-[#D4AF37]/10 transition ${uploading ? "opacity-60 pointer-events-none" : ""}`}
              >
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                {uploading ? "Uploading..." : "Upload from device"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={onFile}
                  className="hidden"
                  data-testid="form-image-file"
                />
              </label>
            </div>
          </div>
          {form.image_url && (
            <img
              src={form.image_url}
              alt="preview"
              className="w-24 h-24 rounded-lg object-cover border border-white/10"
            />
          )}
        </div>
      </div>
    </div>
  );
}

function PayoutDialog({ prop, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("Monthly rental distribution");

  const submit = async () => {
    try {
      await api.post(`/properties/${prop.id}/payout`, { total_amount: parseFloat(amount), note });
      toast.success("Payout distributed");
      onClose();
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open={!!prop} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B1120] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-heading">Distribute rental payout</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-gray-400">{prop?.name}</div>
          <div>
            <Label>Total amount (NGN)</Label>
            <Input data-testid="payout-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 bg-[#050914] border-white/10" />
            <div className="text-[11px] text-gray-500 mt-1">
              Split proportionally across {prop?.sold_bricks || 0} sold bricks
            </div>
          </div>
          <div>
            <Label>Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 bg-[#050914] border-white/10" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="btn-gold rounded-full" onClick={submit} data-testid="payout-submit-btn">Distribute</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
